/** Thin HTTP and WASM binding for Rust-owned saved Kanban policy; no validation or projection decisions live here.
 * @module apps/web/src/lib/kanbanApi
 */
import { request } from './api';
import { fluxAgentBrowserCore } from './fluxAgentBrowser';
import type { TaskUI, WorkspaceBundleUI } from '@fluxability/types';

/** Optional metadata; activity titles and actions are always shown. */
export type KanbanCardField = 'duration' | 'deadline' | 'priority' | 'bundle';
/** Saved view configuration, validated by identical Rust code on server and browser. */
export interface KanbanSpec {
  /** Schema version, currently1. */
  version: 1;
  /** Trimmed saved name,1..80 characters. */
  name: string;
  /** Existing field used to group tasks. */
  grouping: 'status' | 'priority' | 'bundle';
  /** Stable sort selected in Rust. */
  sort: 'manual' | 'title' | 'due_date' | 'priority';
  /** Content padding only; action targets stay unchanged. */
  density: 'comfortable' | 'compact';
  /** Visible optional card metadata. */
  cardFields: KanbanCardField[];
  /** Existing group presentation overrides. */
  columns: KanbanColumnSetting[];
  /** Closed Rust-owned filters; empty arrays mean unrestricted. */
  filters: {
    /** Existing canonical lifecycle statuses. */
    statuses: string[];
    /** Existing numeric priority groups. */
    priorities: number[];
    /** Tagged bundle keys: bundle:id:<opaque ID> or bundle:unassigned. */
    bundleIds: string[];
  };
  /** CSS-shaped input parsed only by Rust. Never inserted into a style element. */
  css: string;
}
/** One existing group label/order/visibility setting. */
export interface KanbanColumnSetting {
  /** Existing group identity, not a selector. */
  id: string;
  /** Optional custom label. */
  label: string | null;
  /** Optional preferred order 0..31; null preserves natural placement. */
  order: number | null;
  /** Explicit visibility choice. */
  visible: boolean;
}
/** Stored view with revision fence. */
export interface KanbanSavedView {
  /** Owner-scoped ID. */
  id: string;
  /** Validated spec. */
  spec: KanbanSpec;
  /** Positive revision. */
  revision: number;
}
/** Bounded owner state from the service. */
export interface KanbanState {
  /** Authenticated owner echoed by the server for response fencing. */
  ownerId: string;
  /** Valid named views. */
  views: KanbanSavedView[];
  /** Active valid ID or null for default. */
  activeId: string | null;
  /** CAS revision of selection. */
  selectionRevision: number;
  /** Safe built-in configuration from Rust. */
  defaultSpec: KanbanSpec;
  /** Invalid retained rows omitted from rendering. */
  invalidCount: number;
}
/** Typed paint output. UI assigns each property explicitly to its permitted target. */
export interface KanbanPaint {
  /** Opaque validated background. */
  backgroundColor: string | null;
  /** Opaque validated border. */
  borderColor: string | null;
  /** Whole-pixel radius 0..24. */
  borderRadius: number | null;
  /** Whole-pixel padding4..24. */
  padding: number | null;
  /** Whole-pixel board gap4..24. */
  gap: number | null;
  /** Heading font size 12..24px. */
  fontSize: number | null;
  /** Heading weight400/500/600/700. */
  fontWeight: number | null;
}
/** Ordered group emitted by Rust. */
export interface KanbanProjectedColumn {
  /** Stable group key. */
  id: string;
  /** Escaped visible heading. */
  title: string;
  /** Real status drop target, or null in other grouping modes. */
  status: 'todo' | 'in_progress' | 'done' | null;
  /** Ordered IDs to look up in the existing task store. */
  taskIds: string[];
}
/** Complete local Rust projection. */
export interface KanbanProjection {
  /** Validated spec. */
  spec: KanbanSpec;
  /** Paint regions with closed properties. */
  styles: {
    /** Board paint. */
    board: KanbanPaint;
    /** Column frame paint. */
    column: KanbanPaint;
    /** Heading typography. */
    columnTitle: KanbanPaint;
    /** Card frame paint. */
    card: KanbanPaint;
  };
  /** Visible ordered groups. */
  columns: KanbanProjectedColumn[];
  /** Count excluded by filters or column visibility. */
  hiddenCount: number;
}
/** Rust projection result; errors are displayed by the editor. */
export type KanbanProjectionResult =
  | {
      /** Valid projection available. */ ok: true;
      /** Canonical local result. */ projection: KanbanProjection;
    }
  | { /** Input rejected. */ ok: false; /** Safe actionable policy error. */ error: string };
/** Binding: forward current store data to the already initialized shared Rust WASM module. */
export function projectKanban(
  spec: KanbanSpec | null,
  tasks: TaskUI[],
  bundles: WorkspaceBundleUI[]
): KanbanProjectionResult {
  return JSON.parse(
    fluxAgentBrowserCore().kanban_project(JSON.stringify({ spec, tasks, bundles }))
  ) as KanbanProjectionResult;
}
/** Thin credentialed transport; server owns all quotas, validation and revisions. */
export const kanbanApi = {
  /** Read owner state. */
  list: (owner: string) => request<KanbanState>(`/kanban-views?owner=${encodeURIComponent(owner)}`),
  /** Create a named configuration. */
  create: (owner: string, spec: KanbanSpec) =>
    request<{ /** Created view. */ view: KanbanSavedView }>(
      `/kanban-views?owner=${encodeURIComponent(owner)}`,
      {
        method: 'POST',
        body: JSON.stringify({ spec }),
      }
    ),
  /** Replace the revision shown in an editor. */
  update: (owner: string, id: string, revision: number, spec: KanbanSpec) =>
    request<{ /** Updated view. */ view: KanbanSavedView }>(
      `/kanban-views/${encodeURIComponent(id)}?owner=${encodeURIComponent(owner)}`,
      { method: 'PUT', body: JSON.stringify({ spec, revision }) }
    ),
  /** Delete only the reviewed revision. */
  delete: (owner: string, id: string, revision: number) =>
    request<{ /** Successful removal. */ deleted: boolean }>(
      `/kanban-views/${encodeURIComponent(id)}?owner=${encodeURIComponent(owner)}`,
      { method: 'DELETE', body: JSON.stringify({ revision }) }
    ),
  /** Activate an owned exact revision or the default. */
  select: (
    owner: string,
    id: string | null,
    viewRevision: number | null,
    selectionRevision: number
  ) =>
    request<KanbanState>(`/kanban-views/active?owner=${encodeURIComponent(owner)}`, {
      method: 'PUT',
      body: JSON.stringify({ id, viewRevision, selectionRevision }),
    }),
};
/** Binding: invalidate boards; an optional opaque mount token lets the sender ignore its own notification. No account or view data is transmitted. */
export function notifyKanbanChanged(source?: string): void {
  window.dispatchEvent(new CustomEvent('fluxability-kanban-changed', { detail: source }));
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel('fluxability-kanban');
    channel.postMessage(source ?? null);
    channel.close();
  }
}
